import { useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useProducts } from "../../context/ProductContext";
import { cloudinaryService } from "../../services/cloudinaryService";
import { X, Plus, Trash2 } from "lucide-react";
import { CATEGORIES } from "../../constants/categories";
// ── Catégories + type de variante ─────────────────────────────


const INITIAL = { name: "", description: "", sous_description: "", category: "" };
const EMPTY_VARIANT = { label: "", price: "", stock: "" };
const SOUS_DESC_MAX = 200;

export default function AddProduct() {
    const { role } = useAuth();
    const { createProduct } = useProducts();

    const [form, setForm] = useState(INITIAL);
    const [variants, setVariants] = useState([{ ...EMPTY_VARIANT }]);
    const [previews, setPreviews] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadedUrls, setUploadedUrls] = useState([]);
    const fileInputRef = useRef(null);

    const selectedCat = CATEGORIES.find(c => c.value === form.category);
    const cleanText = (text = "") =>
        text
            .replace(/[\u2018\u2019\u201A\u201B]/g, "'")   // guillemets simples courbes
            .replace(/[\u201C\u201D\u201E\u201F]/g, '"')   // guillemets doubles courbes
            .replace(/[\u2013\u2014]/g, "-")               // tirets longs
            .replace(/\u2022/g, "•")                       // puces
            .replace(/\u00A0/g, " ")                       // espace insécable
            .replace(/[\u200B-\u200D\uFEFF]/g, "")         // caractères invisibles
            .replace(/\r\n?/g, "\n")                       // fins de ligne Windows
            .replace(/[ \t]+/g, " ")                       // espaces multiples
            .replace(/\n{3,}/g, "\n\n")                    // lignes vides en trop
            .trim();

    const limitLength = (text, max) => text.slice(0, max);
    // ── Champs texte ──────────────────────────────────────────
    const handleChange = (e) => {
        const { name, value } = e.target;
        const next = name === "sous_description" ? limitLength(value, SOUS_DESC_MAX) : value;
        setForm((prev) => ({ ...prev, [name]: next }));
    };

    // ── Changer de catégorie : reset des variantes ────────────
    const handleCategoryChange = (e) => {
        setForm(prev => ({ ...prev, category: e.target.value }));
        setVariants([{ ...EMPTY_VARIANT }]);
    };

    // ── Gestion des variantes ─────────────────────────────────
    const updateVariant = (i, field, value) =>
        setVariants(prev => prev.map((v, idx) => (idx === i ? { ...v, [field]: value } : v)));

    const addVariant = () => setVariants(prev => [...prev, { ...EMPTY_VARIANT }]);

    const removeVariant = (i) =>
        setVariants(prev => prev.filter((_, idx) => idx !== i));

    // ── Sélection images ──────────────────────────────────────
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files).slice(0, 5);
        setPreviews(files.map(file => ({
            id: Math.random(),
            url: URL.createObjectURL(file),
            file,
        })));
        setUploadedUrls([]);
    };

    const removePreview = (id) => {
        setPreviews(prev => prev.filter(p => p.id !== id));
        setUploadedUrls([]);
    };
    const handlePasteSousDesc = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text/plain");
        const cleaned = cleanText(pasted);

        const target = e.currentTarget;
        const start = target.selectionStart;
        const end = target.selectionEnd;
        const current = form.sous_description;

        const next = limitLength(
            current.slice(0, start) + cleaned + current.slice(end),
            SOUS_DESC_MAX
        );

        setForm((prev) => ({ ...prev, sous_description: next }));
    };
    // ── Upload Cloudinary ─────────────────────────────────────
    const handleUploadImages = async () => {
        if (previews.length === 0) {
            alert("Sélectionnez au moins 1 image");
            return;
        }
        setUploading(true);
        setUploadProgress(0);
        try {
            const urls = await cloudinaryService.uploadMultiple(
                previews.map(p => p.file),
                (progress) => setUploadProgress(Math.round(progress))
            );
            setUploadedUrls(urls);
            alert(`✅ ${urls.length} image(s) uploadée(s)`);
        } catch (error) {
            console.error("Erreur upload:", error);
            alert("❌ Erreur lors de l'upload");
        } finally {
            setUploading(false);
        }
    };

    // ── Soumettre ─────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (uploadedUrls.length === 0) {
            alert("Uploadez d'abord les images");
            return;
        }
        const sousDesc = cleanText(form.sous_description);

        const cleanVariants = variants
            .filter(v => v.label.trim() !== "" && v.price !== "")
            .map((v, i) => ({
                id: `v${i + 1}`,
                label: v.label.trim(),
                price: parseFloat(v.price),
                stock: parseInt(v.stock, 10) || 0,
            }));

        if (cleanVariants.length === 0) {
            alert("Ajoutez au moins une variante avec un prix");
            return;
        }
        if (sousDesc.length > SOUS_DESC_MAX) {
            alert(`La sous-description ne doit pas dépasser ${SOUS_DESC_MAX} caractères.`);
            return;
        }

        try {
            await createProduct({
                name: form.name,
                description: form.description,
                sous_description: form.sous_description,
                category: form.category,
                freeShipping: form.freeShipping,

                variantType: selectedCat?.variantType,
                variants: cleanVariants,
                price: Math.min(...cleanVariants.map(v => v.price)),
                images: uploadedUrls,
                mainImage: uploadedUrls[0],
            });

            setForm(INITIAL);
            setVariants([{ ...EMPTY_VARIANT }]);
            setPreviews([]);
            setUploadedUrls([]);
            setUploadProgress(0);
            if (fileInputRef.current) fileInputRef.current.value = "";

            alert("✅ Produit créé avec succès !");
        } catch (error) {
            console.error("Erreur création produit:", error);
            alert("❌ Erreur lors de la création du produit");
        }
    };

    if (role !== "admin") return <p>Accès refusé</p>;

    return (
        <div className="card border-0 rounded-4 p-4 mt-4 mb-5"
            style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>

            <h4 className="mb-4 fw-bold">➕ Ajouter un produit</h4>

            <form onSubmit={handleSubmit}>

                {/* Nom */}
                <div className="mb-3">
                    <label className="form-label fw-semibold small">Nom *</label>
                    <input name="name" value={form.name} onChange={handleChange}
                        type="text" className="form-control rounded-3"
                        placeholder="Ex: Relief Sun Duo" required />
                </div>

                {/* Description */}
                <div className="mb-3">
                    <label className="form-label fw-semibold small">Description *</label>
                    <textarea name="description" value={form.description} onChange={handleChange}
                        rows={3} className="form-control rounded-3"
                        placeholder="Décrivez le produit..." required />
                </div>

                <div className="mb-3">
                    <label className="form-label fw-semibold small">
                        Sous-description
                        <span className="text-muted fw-normal ms-2">
                            ({form.sous_description.length}/{SOUS_DESC_MAX} caractères)
                        </span>
                    </label>

                    <textarea
                        name="sous_description"
                        value={form.sous_description}
                        onChange={handleChange}
                        onPaste={handlePasteSousDesc}
                        rows={4}
                        maxLength={SOUS_DESC_MAX}
                        className="form-control rounded-3"
                        placeholder="Collez ou saisissez la sous-description..."
                    />

                    <div className="form-text">
                        Max {SOUS_DESC_MAX} caractères. Le texte collé depuis Word est nettoyé automatiquement.
                    </div>
                </div>
                {/* livraison  */}
                <div className="mb-3">
                    <label className="form-label fw-semibold small">livraison </label>
                    <input name="name" value={form.freeShipping} onChange={handleChange}
                        type="text" className="form-control rounded-3"
                        placeholder="Ex: Relief Sun Duo" required />
                </div>

                {/* Catégorie */}
                <div className="mb-4">
                    <label className="form-label fw-semibold small">Catégorie *</label>
                    <select name="category" value={form.category} onChange={handleCategoryChange}
                        className="form-select rounded-3" required>
                        <option value="" disabled>-- Choisir une catégorie --</option>
                        {CATEGORIES.map(c => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                    </select>
                </div>

                {/* ── VARIANTES (apparaissent après choix catégorie) ── */}
                {selectedCat && (
                    <div className="mb-4 p-3 bg-light rounded-3">
                        <h6 className="fw-bold mb-3">
                            {selectedCat.variantLabel} et prix *
                        </h6>

                        {variants.map((v, i) => (
                            <div key={i} className="d-flex gap-2 mb-2 align-items-center">
                                <input
                                    className="form-control"
                                    placeholder={selectedCat.placeholder}
                                    value={v.label}
                                    onChange={e => updateVariant(i, "label", e.target.value)}
                                    required
                                />
                                <input
                                    className="form-control"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="Prix (MAD)"
                                    value={v.price}
                                    onChange={e => updateVariant(i, "price", e.target.value)}
                                    required
                                    style={{ maxWidth: 140 }}
                                />
                                <input
                                    className="form-control"
                                    type="number"
                                    min="0"
                                    placeholder="Stock"
                                    value={v.stock}
                                    onChange={e => updateVariant(i, "stock", e.target.value)}
                                    style={{ maxWidth: 100 }}
                                />
                                {variants.length > 1 && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger"
                                        onClick={() => removeVariant(i)}
                                        aria-label="Supprimer la variante"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                )}
                            </div>
                        ))}

                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary mt-2 d-flex align-items-center gap-1"
                            onClick={addVariant}
                        >
                            <Plus size={14} /> Ajouter une {selectedCat.variantLabel.toLowerCase()}
                        </button>
                    </div>
                )}

                {/* ÉTAPE 1 : IMAGES */}
                <div className="mb-4 p-3 bg-light rounded-3">
                    <h6 className="fw-bold mb-3">📸 Sélectionner les images</h6>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="form-control rounded-3"
                        disabled={uploading}
                    />
                    <small className="form-text text-muted mt-2 d-block">
                        Max 5 images (JPG, PNG, WebP)
                    </small>
                </div>

                {/* Aperçu */}
                {previews.length > 0 && (
                    <div className="mb-4">
                        <label className="fw-semibold small d-block mb-2">
                            🖼️ Aperçu ({previews.length}/5)
                        </label>
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
                            gap: "12px"
                        }}>
                            {previews.map((preview, i) => (
                                <div key={preview.id} style={{
                                    position: "relative",
                                    borderRadius: "8px",
                                    overflow: "hidden",
                                    border: "2px solid #e5e5e5"
                                }}>
                                    <img src={preview.url} alt={`preview-${i}`}
                                        style={{ width: "100%", height: "100px", objectFit: "cover" }} />
                                    <button
                                        type="button"
                                        onClick={() => removePreview(preview.id)}
                                        style={{
                                            position: "absolute", top: 4, right: 4,
                                            background: "rgba(0,0,0,0.7)", border: "none",
                                            borderRadius: 4, color: "white", cursor: "pointer", padding: 4
                                        }}
                                    >
                                        <X size={14} />
                                    </button>
                                    <span style={{
                                        position: "absolute", bottom: 4, left: 4,
                                        background: "rgba(0,0,0,0.7)", color: "white",
                                        fontSize: 11, padding: "2px 6px", borderRadius: 3
                                    }}>
                                        {i + 1}/5
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ÉTAPE 2 : UPLOAD */}
                <div className="mb-4 p-3 bg-warning bg-opacity-10 rounded-3">
                    <h6 className="fw-bold mb-3">☁️ Upload les images</h6>

                    {uploading && (
                        <div className="mb-3">
                            <div className="progress" style={{ height: 8 }}>
                                <div className="progress-bar"
                                    style={{ width: `${uploadProgress}%`, background: "#ff9800", transition: "width .3s" }} />
                            </div>
                            <small className="text-muted">{uploadProgress}% complété</small>
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={handleUploadImages}
                        disabled={previews.length === 0 || uploading}
                        className="btn fw-bold px-4 rounded-3"
                        style={{ background: uploadedUrls.length > 0 ? "#28a745" : "#ff9800", color: "white" }}
                    >
                        {uploading
                            ? `⏳ Upload en cours... ${uploadProgress}%`
                            : uploadedUrls.length > 0
                                ? `✅ ${uploadedUrls.length} image(s) uploadée(s)`
                                : "☁️ Upload"}
                    </button>
                </div>

                {/* ÉTAPE 3 : CRÉER */}
                {uploadedUrls.length > 0 && (
                    <div className="mb-4 p-3 bg-success bg-opacity-10 rounded-3">
                        <h6 className="fw-bold mb-3">✅ Créer le produit</h6>
                        <button
                            type="submit"
                            className="btn fw-bold text-white px-4 rounded-3 w-100"
                            style={{ background: "#28a745" }}
                        >
                            Créer le produit
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
}